import React, { useState, useEffect } from "react";
import {
  X,
  Save,
  AlertCircle,
  RefreshCcw,
  Plus,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { adminAddonService } from "../../../../services/api/settings";
import { toast } from "sonner";

interface EditAddonModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  onSuccess: () => void;
}

interface AddonData {
  name: string;
  displayName: string;
  description: string;
  price: number;
  monthlyPrice?: number;
  yearlyPrice?: number;
  category: string;
  billingType: string;
  isActive: boolean;
  points?: string[];
  serviceType: string;
  syncPrice: boolean;
}

const EditAddonModal: React.FC<EditAddonModalProps> = ({
  isOpen,
  onClose,
  data,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [newPoint, setNewPoint] = useState("");
  const [formData, setFormData] = useState<AddonData>({
    name: "",
    displayName: "",
    description: "",
    price: 0,
    monthlyPrice: 0,
    yearlyPrice: 0,
    category: "",
    billingType: "",
    isActive: true,
    points: [] as string[],
    serviceType: "",
    syncPrice: false,
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || data.title || "",
        displayName: data.displayName || data.name || data.title || "",
        description: data.description || "",
        price: data.price || 0,
        // monthlyPrice: parseFloat(data.monthlyPrice || "0"),
        // yearlyPrice: parseFloat(data.yearlyPrice || "0"),
        category: data.category || data.tier || "",
        serviceType: data.serviceType || "",
        billingType: data.billingType || "",
        isActive: data.isActive !== undefined ? data.isActive : true,
        // points: Array.isArray(data.points)
        //   ? [...data.points]
        //   : Array.isArray(data.features)
        //     ? [...data.features]
        //     : [],
        syncPrice: false,
      });
    }
  }, [data]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload: any = {
        ...formData,
        syncPrice: true,
      };
      await adminAddonService.update(data.id, payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // const addPoint = () => {
  //   if (newPoint.trim()) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       points: [...prev.points, newPoint.trim()],
  //     }));
  //     setNewPoint("");
  //   }
  // };

  // const removePoint = (index: number) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     points: prev.points.filter((_, i) => i !== index),
  //   }));
  // };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Edit Service/Feature
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              Update details for {data.name || data.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 h-full flex flex-col"
        >
          <div className="p-6 space-y-6 flex-1">
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Internal Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.displayName}
                    onChange={(e) =>
                      setFormData({ ...formData, displayName: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
                />
              </div>

              {/* Pricing Grid */}
              <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100 space-y-4">
                <div className="flex items-center gap-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">
                    Pricing Configuration
                  </h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="syncPrice"
                      name="syncPrice"
                      checked={formData.syncPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          syncPrice: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 cursor-pointer"
                    />
                    <label
                      htmlFor="syncPrice"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Edit Prices
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">
                      Base Price (Kobo)
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price: parseInt(e.target.value),
                        })
                      }
                      disabled={!formData.syncPrice}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">
                      Monthly Price
                    </label>
                    <input
                      type="number"
                      value={formData.monthlyPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          monthlyPrice: parseFloat(e.target.value),
                        })
                      }
                      disabled={!formData.syncPrice}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">
                      Yearly Price
                    </label>
                    <input
                      type="number"
                      value={formData.yearlyPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          yearlyPrice: parseFloat(e.target.value),
                        })
                      }
                      disabled={!formData.syncPrice}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* Points Manager */}
              {/* <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">
                    Key Features (Points)
                  </label>
                  <span className="text-[10px] font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {formData.points.length} Points
                  </span>
                </div>

                <div className="space-y-2">
                  {formData.points.map((point, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 group animate-in slide-in-from-left-2 duration-200"
                    >
                      <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-50 border border-transparent group-hover:border-primary/20 rounded-xl transition-all">
                        <CheckCircle2 size={16} className="text-emerald-500" />
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => {
                            const newPoints = [...formData.points];
                            newPoints[index] = e.target.value;
                            setFormData({ ...formData, points: newPoints });
                          }}
                          className="flex-1 bg-transparent border-none text-sm font-medium text-gray-700 outline-none p-0"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removePoint(index)}
                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="text"
                      placeholder="Add a new feature point..."
                      value={newPoint}
                      onChange={(e) => setNewPoint(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addPoint();
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl text-sm focus:border-primary/30 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={addPoint}
                      disabled={!newPoint.trim()}
                      className="p-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 disabled:bg-gray-300"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </div> */}

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                >
                  <option value="">Select Category</option>
                  <option value="INTEGRATION">Integration</option>
                  <option value="DEPLOYMENT_COST">Deployment Cost</option>
                  <option value="STARTER">Starter</option>
                  <option value="GROWTH">Growth</option>
                  <option value="SCALE">Scale</option>
                  <option value="ENTERPRISE">Enterprise</option>
                  <option value="ADDON">Addon</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full border-2 border-transparent hover:border-primary/10 transition-all">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary transition-all"
                  />
                  <span className="text-sm font-bold text-gray-700">
                    Active Status
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-bold disabled:opacity-50"
            >
              {loading ? (
                <RefreshCcw size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              <span>{loading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAddonModal;
